import { DEBUG, IS_SSG_ATTR_ENABLED, SSG_ATTR_HOST } from '../config';
import { pause } from './schedulers';

type AttrEvent = 'access' | 'register' | 'login' | 'first_message';

type AttrPayload = {
  event_id: string;
  event: AttrEvent;
  ts: number;
  page: string;
  channelcode?: string;
  subid?: string;
  user_id?: string;
};

type QueueItem = {
  payload: AttrPayload;
  attempts: number;
};

type PostResult =
  | { kind: 'ok'; body: unknown }
  | { kind: 'rejected'; code: number }
  | { kind: 'retryable' };

type SendOutcome = 'ok' | 'dropped' | 'queued';

type AttrParams = {
  channelcode: string;
  subid: string;
};

const EVENT_URL = `${SSG_ATTR_HOST}/api/v1/event`;
const BATCH_URL = `${SSG_ATTR_HOST}/api/v1/event/batch`;

const ATTR_STORE_KEY = 'rt_attr';
const SESSION_ID_KEY = 'ssg_attr_sid';
const QUEUE_KEY = 'ssg_attr_queue';
const SENT_KEY_PREFIX = 'ssg_attr_sent_';

const QUEUE_MAX = 50;
const QUEUE_MAX_ATTEMPTS = 5;
const EVENT_ID_MAX_LENGTH = 64;

const CODE_OK = 0;
const CODE_NO_VISIT = 40004;
const CODE_RATE_LIMITED = 42901;
const CODE_UNKNOWN = -1;

const NO_VISIT_RETRY_MS = [1000, 3000, 10000];
const ACCESS_WAIT_TIMEOUT_MS = 8000;
const LOGIN_DEFER_MS = 2500;

const inFlight = new Set<string>();

let accessPromise: Promise<void> | undefined;
let isFlushing = false;
let fallbackSessionId: string | undefined;
let hasRegisteredInThisSession = false;

function createId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function logAttr(...args: unknown[]) {
  if (!DEBUG) return;
  // eslint-disable-next-line no-console
  console.info('[SSG][Attr]', ...args);
}

function readFromUrl(): AttrParams | undefined {
  try {
    const lowerCased: Record<string, string> = {};
    new URLSearchParams(window.location.search).forEach((value, key) => {
      lowerCased[key.toLowerCase()] = value;
    });

    const channelcode = lowerCased.channelcode || '';
    if (!channelcode) return undefined;

    return { channelcode, subid: lowerCased.subid || '' };
  } catch {
    return undefined;
  }
}

function getAttrParams(): AttrParams {
  const fromUrl = readFromUrl();
  if (fromUrl) {
    try {
      window.sessionStorage.setItem(ATTR_STORE_KEY, JSON.stringify(fromUrl));
    } catch {
      // Ignore.
    }
    return fromUrl;
  }

  try {
    const raw = window.sessionStorage.getItem(ATTR_STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.channelcode === 'string') {
        return { channelcode: parsed.channelcode, subid: typeof parsed.subid === 'string' ? parsed.subid : '' };
      }
    }
  } catch {
    // Ignore.
  }

  return { channelcode: '', subid: '' };
}

function hasAttrParams(): boolean {
  const { channelcode, subid } = getAttrParams();
  return Boolean(channelcode && subid);
}

function getSessionId(): string {
  try {
    const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;

    const created = createId();
    window.sessionStorage.setItem(SESSION_ID_KEY, created);
    return created;
  } catch {
    if (!fallbackSessionId) {
      fallbackSessionId = createId();
    }
    return fallbackSessionId;
  }
}

function getPageUrl(): string {
  try {
    const { origin, pathname, search } = window.location;
    return `${origin}${pathname}${search}`;
  } catch {
    return '';
  }
}

function getSentUid(event: AttrEvent): string | undefined {
  try {
    return window.localStorage.getItem(`${SENT_KEY_PREFIX}${event}`) ?? undefined;
  } catch {
    return undefined;
  }
}

function markSent(event: AttrEvent, uid: string) {
  try {
    window.localStorage.setItem(`${SENT_KEY_PREFIX}${event}`, uid);
  } catch {
    // Ignore.
  }
}

function readQueue(): QueueItem[] {
  try {
    const raw = window.localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((item) => item && typeof item.payload === 'object' && typeof item.payload.event_id === 'string')
      .map((item) => ({
        payload: item.payload as AttrPayload,
        attempts: typeof item.attempts === 'number' ? item.attempts : 0,
      }))
      .slice(-QUEUE_MAX);
  } catch {
    return [];
  }
}

function writeQueue(items: QueueItem[]) {
  try {
    if (!items.length) {
      window.localStorage.removeItem(QUEUE_KEY);
      return;
    }
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(-QUEUE_MAX)));
  } catch {
    // Ignore.
  }
}

function pushQueue(payload: AttrPayload, attempts = 0) {
  if (attempts >= QUEUE_MAX_ATTEMPTS) {
    logAttr('丢弃', payload.event, payload.event_id, `attempts=${attempts}`);
    return;
  }

  const items = readQueue();
  if (attempts === 0 && items.some((item) => item.payload.event_id === payload.event_id)) {
    return;
  }

  const kept = items.filter((item) => item.payload.event_id !== payload.event_id);
  kept.push({ payload, attempts });
  writeQueue(kept);
  logAttr('预写入队', payload.event, payload.event_id, `attempts=${attempts}`);
}

function removeFromQueue(eventId: string) {
  const items = readQueue();
  const kept = items.filter((item) => item.payload.event_id !== eventId);
  if (kept.length === items.length) return;
  writeQueue(kept);
}

function readCode(body: unknown): number {
  if (body && typeof body === 'object' && 'code' in body) {
    const value = (body as { code?: unknown }).code;
    if (typeof value === 'number') return value;
  }
  return CODE_UNKNOWN;
}

function readDuplicate(body: unknown): boolean {
  if (body && typeof body === 'object' && 'duplicate' in body) {
    return Boolean((body as { duplicate?: unknown }).duplicate);
  }
  return false;
}

function readBound(body: unknown): boolean | undefined {
  if (body && typeof body === 'object' && 'attribution' in body) {
    const attribution = (body as { attribution?: unknown }).attribution;
    if (attribution && typeof attribution === 'object' && 'bound' in attribution) {
      return Boolean((attribution as { bound?: unknown }).bound);
    }
  }
  return undefined;
}

function readBatchCodes(body: unknown): Record<string, number> {
  const codes: Record<string, number> = {};
  if (!body || typeof body !== 'object' || !('data' in body)) return codes;

  const data = (body as { data?: unknown }).data;
  if (!data || typeof data !== 'object' || !('results' in data)) return codes;

  const results = (data as { results?: unknown }).results;
  if (!Array.isArray(results)) return codes;

  results.forEach((result) => {
    if (result && typeof result === 'object' && typeof (result as { event_id?: unknown }).event_id === 'string') {
      codes[(result as { event_id: string }).event_id] = readCode(result);
    }
  });

  return codes;
}

async function post(url: string, body: unknown): Promise<PostResult> {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain;charset=UTF-8',
      },
      body: JSON.stringify(body),
      keepalive: true,
    });

    if (response.status >= 500 || response.status === 429) {
      return { kind: 'retryable' };
    }

    const parsed = await response.json().catch(() => undefined);
    const code = readCode(parsed);

    if (code === CODE_OK) return { kind: 'ok', body: parsed };
    if (code === CODE_RATE_LIMITED) return { kind: 'retryable' };

    return { kind: 'rejected', code };
  } catch {
    return { kind: 'retryable' };
  }
}

async function flushQueue(exceptEventId?: string): Promise<void> {
  if (!IS_SSG_ATTR_ENABLED || isFlushing) return;

  const all = readQueue();
  const held = exceptEventId ? all.filter((item) => item.payload.event_id === exceptEventId) : [];
  const items = exceptEventId ? all.filter((item) => item.payload.event_id !== exceptEventId) : all;
  if (!items.length) return;

  isFlushing = true;
  writeQueue(held);

  try {
    const result = await post(BATCH_URL, { events: items.map((item) => item.payload) });

    if (result.kind === 'retryable') {
      const retried = items
        .map((item) => ({ payload: item.payload, attempts: item.attempts + 1 }))
        .filter((item) => item.attempts < QUEUE_MAX_ATTEMPTS);
      writeQueue(retried.concat(readQueue()));
      return;
    }

    if (result.kind === 'rejected') {
      logAttr('批量被拒', result.code);
      return;
    }

    const codes = readBatchCodes(result.body);
    const kept = items
      .filter((item) => codes[item.payload.event_id] === CODE_NO_VISIT)
      .map((item) => ({ payload: item.payload, attempts: item.attempts + 1 }))
      .filter((item) => item.attempts < QUEUE_MAX_ATTEMPTS);

    logAttr('补发', `${items.length} 条`, `留下 ${kept.length} 条`);
    writeQueue(kept.concat(readQueue()));
  } finally {
    isFlushing = false;
  }
}

async function sendEvent(payload: AttrPayload): Promise<SendOutcome> {
  if (!IS_SSG_ATTR_ENABLED) return 'dropped';

  pushQueue(payload);

  let attempt = 0;

  for (;;) {
    const result = await post(EVENT_URL, payload);

    if (result.kind === 'ok') {
      const bound = readBound(result.body);
      const boundLabel = bound === undefined ? '' : `bound=${bound}`;
      const dupLabel = readDuplicate(result.body) ? '(重复，已去重)' : '';
      logAttr('已上报', payload.event, payload.event_id, boundLabel, dupLabel);
      removeFromQueue(payload.event_id);
      return 'ok';
    }

    if (result.kind === 'retryable') {
      logAttr('发送失败，留在队列里', payload.event, payload.event_id);
      return 'queued';
    }

    if (result.code !== CODE_NO_VISIT) {
      logAttr('被拒', payload.event, payload.event_id, `code=${result.code}`);
      removeFromQueue(payload.event_id);
      return 'dropped';
    }

    if (attempt >= NO_VISIT_RETRY_MS.length) {
      logAttr('40004 重试用尽，留在队列里', payload.event, payload.event_id);
      return 'queued';
    }

    await pause(NO_VISIT_RETRY_MS[attempt]);
    attempt += 1;
    await flushQueue(payload.event_id);
  }
}

async function waitForAccess(): Promise<void> {
  if (!accessPromise) return;
  await Promise.race([accessPromise, pause(ACCESS_WAIT_TIMEOUT_MS)]);
}

function buildEventId(uid: string, event: AttrEvent): string {
  return `${uid}:${event}`.slice(0, EVENT_ID_MAX_LENGTH);
}

function reportConversion(event: 'register' | 'login' | 'first_message', uid: string) {
  if (!IS_SSG_ATTR_ENABLED || !uid) return;
  if (getSentUid(event) === uid) return;
  if (!hasAttrParams()) {
    logAttr('没拿到 channelcode/subid，不报', event, uid);
    return;
  }

  const eventId = buildEventId(uid, event);
  if (inFlight.has(eventId)) return;
  inFlight.add(eventId);

  const payload: AttrPayload = {
    event_id: eventId,
    event,
    ts: Date.now(),
    page: getPageUrl(),
    user_id: uid,
  };

  if (event === 'register') {
    hasRegisteredInThisSession = true;

    const { channelcode, subid } = getAttrParams();
    payload.channelcode = channelcode;
    payload.subid = subid;

    pushQueue(payload);
  }

  void (async () => {
    try {
      if (event === 'register') {
        await waitForAccess();
      }

      if (event === 'login') {
        await pause(LOGIN_DEFER_MS);
        if (hasRegisteredInThisSession) {
          logAttr('本次会话是注册会话，不报 login', uid);
          return;
        }
      }

      if (await sendEvent(payload) === 'ok') {
        markSent(event, uid);
      }
    } finally {
      inFlight.delete(eventId);
    }
  })();
}

export function initAttrReport(): void {
  if (!IS_SSG_ATTR_ENABLED) return;

  void flushQueue();

  const { channelcode, subid } = getAttrParams();

  if (!channelcode || !subid) {
    accessPromise = Promise.resolve();
    logAttr('没拿到 channelcode/subid，本次不上报', `channelcode=${channelcode || '(空)'}`, `subid=${subid || '(空)'}`);
    return;
  }

  accessPromise = sendEvent({
    event_id: `a:${getSessionId()}`.slice(0, EVENT_ID_MAX_LENGTH),
    event: 'access',
    ts: Date.now(),
    page: getPageUrl(),
    channelcode,
    subid,
  }).then(() => undefined);
}

export function reportAttrRegister(uid: string): void {
  reportConversion('register', uid);
}

export function reportAttrLogin(uid: string): void {
  reportConversion('login', uid);
}

export function reportAttrFirstMessage(uid: string): void {
  reportConversion('first_message', uid);
}
